import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { niceName } from "@/lib/format";
import { ProcessCard } from "@/components/ProcessCard";
import { GestaoProcessos } from "@/components/GestaoProcessos";
import { Input } from "@/components/ui/input";
import { Search, Plus, FolderOpen, Clock, AlertTriangle, Sparkles, FileSignature } from "lucide-react";

import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function StatTile({ icon: Icon, label, value, accent, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: i * 0.06, duration: 0.4 }}
      className="bg-white rounded-2xl p-5 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex items-center gap-4"
      data-testid={`stat-${label}`}
    >
      <span className="grid place-items-center w-11 h-11 rounded-xl shrink-0" style={{ backgroundColor: accent + "1a", color: accent }}>
        <Icon size={20} strokeWidth={1.5} />
      </span>
      <div>
        <div className="font-heading text-3xl text-ec-green leading-none">{value}</div>
        <div className="text-xs text-slate-500 mt-1">{label}</div>
      </div>
    </motion.div>
  );
}


export default function Processos() {
  const { user } = useAuth();
  const [vista, setVista] = useState("gestao");
  if (user?.role === "gestor") return <GestaoProcessos />;
  if (user?.role !== "direccao") return <ConsultorProcessos />;
  return (
    <div className="space-y-4">
      <div className="flex gap-1 bg-white rounded-xl p-1 border border-slate-100 w-fit" data-testid="vista-toggle">
        {[["gestao", "Vista da gestão processual"], ["consultor", "Vista dos consultores"]].map(([k, l]) => (
          <button key={k} onClick={() => setVista(k)} data-testid={`vista-${k}`}
            className={`px-3.5 py-1.5 rounded-lg text-sm transition-colors ${vista === k ? "bg-slate-100 text-slate-900 font-semibold" : "text-slate-500 hover:bg-slate-50"}`}>{l}</button>
        ))}
      </div>
      {vista === "gestao" ? <GestaoProcessos isDirecao /> : <ConsultorProcessos />}
    </div>
  );
}

function ConsultorProcessos() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [procs, setProcs] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [consultant, setConsultant] = useState("all");
  const [tab, setTab] = useState("vendas");
  const [params, setParams] = useSearchParams();
  const fase = params.get("fase");
  const readOnly = user?.role === "direccao";
  useEffect(() => { if (params.get("consultor")) setConsultant(params.get("consultor")); }, [params]);
  const faseLabel = { angariacao: "Angariação", pronto: "CMI assinado", proposta: "Proposta / CPCV", escritura: "Escritura marcada" }[fase];
  useEffect(() => { if (fase) setTab(["angariacao", "pronto"].includes(fase) ? "angariacoes" : "vendas"); }, [fase]);

  const load = () => {
    const params = { scope: "all" };
    if (consultant !== "all") params.consultant = consultant;
    if (search) params.search = search;
    api.get("/processes", { params }).then(({ data }) => setProcs(data));
  };

  useEffect(() => { api.get("/users").then(({ data }) => setUsers(data)); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search, consultant]);

  const seedExamples = async () => {
    await api.post("/demo/seed-showcase");
    await api.post("/demo/seed-examples");
    toast.success("Processo de demonstração e exemplos criados");
    load();
  };

  const consultores = users.filter((u) => u.role === "consultor" || u.role === "gestor");

  const all = procs || [];
  const groups = {
    angariacoes: all.filter((p) => p.status === "angariacao"),
    vendas: all.filter((p) => p.status === "em_venda"),
    concluidos: all.filter((p) => ["escriturado", "sem_venda"].includes(p.status)),
  };
  const phaseOf = (p) => p.status === "angariacao" ? (p.mediation?.signed_date ? "pronto" : "angariacao") : p.status === "em_venda" ? (p.deed_date ? "escritura" : "proposta") : null;
  const shown = faseLabel ? groups[tab].filter((p) => phaseOf(p) === fase) : groups[tab];

  const stats = (() => {
    const list = groups.vendas;
    const now = new Date();
    return {
      abertos: list.length,
      risco: list.filter((p) => p.days_to_deed != null && p.days_to_deed <= 14).length,
      atencao: list.filter((p) => ["red", "amber"].includes(p.card?.tone)).length,
      escrituras: list.filter((p) => {
        if (!p.deed_date) return false;
        const [y, m] = String(p.deed_date).split("-").map(Number);
        return y === now.getFullYear() && m === now.getMonth() + 1;
      }).length,
    };
  })();

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <h1 className="font-heading text-3xl md:text-4xl text-ec-green">Processos</h1>
      </div>

      {user?.role !== "consultor" && <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile i={0} icon={FolderOpen} label="Vendas em curso" value={stats.abertos} accent="#12332B" />
        <StatTile i={1} icon={Clock} label="Prazos em risco (≤14 dias)" value={stats.risco} accent="#b45309" />
        <StatTile i={2} icon={AlertTriangle} label="A precisar de atenção" value={stats.atencao} accent="#b91c1c" />
        <StatTile i={3} icon={FileSignature} label="Escrituras este mês" value={stats.escrituras} accent="#CFAE6E" />
      </div>}

      <div className="bg-white rounded-2xl p-3 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Pesquisar imóvel, referência, parte…"
            className="pl-9 border-slate-200"
            data-testid="search-input"
          />
        </div>
        {user.role !== "consultor" && (
          <Select value={consultant} onValueChange={setConsultant}>
            <SelectTrigger className="w-full sm:w-52 border-slate-200" data-testid="consultant-filter">
              <SelectValue placeholder="Consultor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os consultores</SelectItem>
              {consultores.map((u) => (
                <SelectItem key={u.id} value={u.id}>{niceName(u.name)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="flex gap-1 bg-white rounded-xl p-1 border border-slate-100 w-fit" data-testid="process-tabs">
        {[["angariacoes", "Angariações"], ["vendas", "Vendas em curso"], ["concluidos", "Concluídos"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-2 rounded-lg text-sm transition-colors ${tab === k ? "bg-ec-green text-white" : "text-slate-600 hover:bg-slate-50"}`}>
            {l} <span className={`ml-1 font-mono text-xs ${tab === k ? "text-ec-gold" : "text-slate-400"}`}>{groups[k].length}</span>
          </button>
        ))}
      </div>
      {faseLabel && (
        <div className="flex items-center gap-2 text-sm" data-testid="phase-filter">
          <span className="px-3 py-1.5 rounded-full bg-ec-green/10 text-ec-green font-medium">Fase: {faseLabel}</span>
          <button onClick={() => setParams({})} className="text-slate-500 underline underline-offset-2 hover:text-ec-green" data-testid="phase-filter-clear">Limpar filtro</button>
        </div>
      )}

      {procs === null ? (
        <div className="text-slate-400 py-20 text-center">A carregar…</div>
      ) : procs.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-slate-100">
          <p className="text-slate-500">Ainda não há processos.</p>
          <div className={`mt-5 flex-col sm:flex-row gap-3 justify-center ${readOnly ? "hidden" : "flex"}`}>
            {user?.role === "consultor" && <button onClick={() => navigate("/novo")} className="inline-flex items-center justify-center gap-2 bg-ec-green text-white px-4 py-2 rounded-lg hover:bg-ec-greenDark transition-colors">
              <Plus size={16} /> Criar o primeiro processo
            </button>}
            {user?.real_platform_admin && user?.agency_is_demo && <button onClick={seedExamples} data-testid="seed-examples-btn" className="inline-flex items-center justify-center gap-2 bg-ec-gold text-ec-green font-semibold px-4 py-2 rounded-lg hover:bg-[#b89b5c] transition-colors">
              <Sparkles size={16} /> Criar processo de demonstração
            </button>}
          </div>
        </div>
      ) : shown.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center border border-slate-100 text-slate-500" data-testid="tab-empty">
          {tab === "angariacoes" ? "Sem angariações ativas." : tab === "vendas" ? "Sem vendas em curso." : "Ainda não há processos concluídos."}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {shown.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 8) * 0.05, duration: 0.4 }}>
              <ProcessCard id={p.id} card={p.card} testid={`process-card-${p.ref}`}
                extra={p.is_example && user?.agency_is_demo ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Exemplo</span> : null} />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
